import { MatrizCostoM2 } from '@/components/dominio/MatrizCostoM2';
import { MultiSelectPopup } from '@/components/filtros/MultiSelectPopup';
import { GestorColumnas } from '@/components/tabla/GestorColumnas';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePartidasCatalogo, useProductividad } from '@/hooks/useAvanceMatriz';
import { type ColumnaConfigItem, useColumnasConfig } from '@/hooks/useColumnasConfig';
import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import type { PartidaSel } from '@/lib/cliente';
import {
  type ExcelFmt,
  type FilaCostosM2Export,
  exportarCostosM2Excel,
} from '@/lib/exportarCostosM2Excel';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple } from '@phosphor-icons/react';
import { type ReactElement, useMemo, useState } from 'react';

type Modo = 'general' | 'mo' | 'matriz';

const MO_ESTANDAR_DEFAULT = 122000;

function fmtMonto(v: number): string {
  if (!v) return '—';
  const neg = v < 0;
  const s = Math.round(Math.abs(v))
    .toLocaleString('es-CR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ');
  return `${neg ? '-' : ''}₡${s}`;
}
function fmtNum(v: number): string {
  if (!v) return '—';
  const neg = v < 0;
  const s = Math.round(Math.abs(v))
    .toLocaleString('es-CR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ');
  return `${neg ? '-' : ''}${s}`;
}
function fmtPct(v: number): string {
  return `${(v * 100).toLocaleString('es-CR', { maximumFractionDigits: 1 })} %`;
}
function fmtIdx(v: number): string {
  if (!v) return '—';
  return v.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Semáforo General sobre % del costo real vs teórico:
 *   > +2 %  → Sobrecosto (rojo)
 *   -20%..+2% → En rango (verde/correcto)
 *   < -20 % → Subcosteo (azul): demasiado bajo, probablemente faltan gastos por registrar.
 */
type Alerta = { txt: string; cls: string; orden: number; titulo: string };
function clasificar(pctSobre: number): Alerta {
  if (pctSobre > 0.02)
    return {
      txt: 'Sobrecosto',
      cls: 'text-red-600',
      orden: 0,
      titulo: 'Costo real sobre el teórico (+2%)',
    };
  if (pctSobre < -0.2)
    return {
      txt: 'Subcosteo',
      cls: 'text-blue-600',
      orden: 2,
      titulo: 'Muy por debajo del teórico (−20%): probable falta de gastos por registrar',
    };
  return {
    txt: 'En rango',
    cls: 'text-emerald-700',
    orden: 1,
    titulo: 'Dentro del rango esperado',
  };
}

// --- Tipos de fila de la matriz (vista árbol) ---
interface FilaObra {
  worksNo: string;
  display: string | null;
  proy: string;
  bloque: string;
  // crudos
  areaM2Total: number;
  m2Construidos: number;
  pctAvance: number;
  costoDirecto: number;
  presupuestoDirecto: number;
  costoAvance: number;
  costoRealXm2: number;
  presupuestoXm2: number;
  costoMO: number;
}

// --- Definición de una columna de datos (dirige render, orden y export) ---
interface Celda {
  texto: string;
  className?: string;
  title?: string;
}
interface ColDef {
  /** Celda para una obra (fila hoja). */
  obra: (o: FilaObra) => Celda;
  /** Celda para un grupo agregado (Proyecto/Bloque/Gran Total). */
  grupo: (lista: FilaObra[]) => Celda;
  /** Valor numérico para ordenar por esta columna (obra). También es el valor
   *  crudo que se exporta a Excel (salvo columnas de texto). */
  sortObra: (o: FilaObra) => number;
  /** Valor numérico para ordenar grupos por esta columna. */
  sortGrupo: (lista: FilaObra[]) => number;
}

/** Formato de cada columna para el export a Excel, por id. */
const FMT_COLUMNA: Record<string, ExcelFmt> = {
  m2Av: 'numero',
  m2Tot: 'numero',
  pctAv: 'porcentaje',
  costoReal: 'numero',
  costoTeo: 'numero',
  delta: 'numero',
  pctSobre: 'porcentaje',
  idxEf: 'indice',
  estado: 'texto',
  moXm2: 'numero',
  estandar: 'numero',
  deltaXm2: 'numero',
  costoMO: 'moneda',
  moTeoCons: 'moneda',
  desviacion: 'moneda',
  idxEfMO: 'indice',
  moTeoTot: 'moneda',
};

// Catálogo (id + etiqueta) por modo — estable a nivel módulo para el hook.
const META_GENERAL: ColumnaConfigItem[] = [
  { id: 'm2Av', etiqueta: 'M² Avanzados' },
  { id: 'm2Tot', etiqueta: 'M² Totales' },
  { id: 'pctAv', etiqueta: '% Avance' },
  { id: 'costoReal', etiqueta: 'Costo Real (₡ x m²)' },
  { id: 'costoTeo', etiqueta: 'Costo Teórico (₡ x m²)' },
  { id: 'delta', etiqueta: 'Delta (₡ x m²)' },
  { id: 'pctSobre', etiqueta: '% Sobre Teórico' },
  { id: 'idxEf', etiqueta: 'Índice Efic.' },
  { id: 'estado', etiqueta: 'Estado' },
];
const META_MO: ColumnaConfigItem[] = [
  { id: 'm2Av', etiqueta: 'M² Avanzados' },
  { id: 'm2Tot', etiqueta: 'M² Totales' },
  { id: 'pctAv', etiqueta: '% Avance' },
  { id: 'moXm2', etiqueta: 'MO x m² Constr.' },
  { id: 'estandar', etiqueta: 'Estándar x m²' },
  { id: 'deltaXm2', etiqueta: 'Delta x m²' },
  { id: 'costoMO', etiqueta: 'Costo M.O.' },
  { id: 'moTeoCons', etiqueta: 'MO Teórica Consumida' },
  { id: 'desviacion', etiqueta: 'Desviación MO' },
  { id: 'idxEfMO', etiqueta: 'Índice Efic. MO' },
  { id: 'moTeoTot', etiqueta: 'MO Teórica Total' },
];
// En M.O. las columnas de m²/avance existen pero arrancan ocultas.
const OCULTAS_MO = ['m2Av', 'm2Tot', 'pctAv'];

const sum = (lista: FilaObra[], f: (o: FilaObra) => number) => lista.reduce((s, o) => s + f(o), 0);

/** Tarea padre = prefijo del task_no antes del primer punto ("1.1"→"1", "CI.PV"→"CI"). */
const padreDe = (taskNo: string) => {
  const i = taskNo.indexOf('.');
  return i < 0 ? taskNo : taskNo.slice(0, i);
};

/** Etiqueta de una partida sin duplicar el código ("1.1 - Fundaciones", no "1.1 — 1.1 - Fundaciones"). */
const etiquetaPartida = (p: { taskNo: string; tareaDisplay: string | null }) =>
  p.tareaDisplay
    ? p.tareaDisplay.startsWith(p.taskNo)
      ? p.tareaDisplay
      : `${p.taskNo} — ${p.tareaDisplay}`
    : p.taskNo;

export function ProductividadPantalla() {
  const [modo, setModo] = useState<Modo>('general');
  const [moEstandar, setMoEstandar] = useState<number>(MO_ESTANDAR_DEFAULT);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  // Filtro de partida LOCAL (jerárquico Tarea Padre → Partida), MULTI-selección.
  // Set vacío = sin filtro. `partidasSel` guarda task_no exactos.
  const [padresSel, setPadresSel] = useState<Set<string>>(new Set());
  const [partidasSel, setPartidasSel] = useState<Set<string>>(new Set());
  // Mostrar solo obras al 100% de avance (redondeado a 100,0 %).
  const [solo100, setSolo100] = useState(false);

  // Config de columnas por modo (persistida por reporte). Se llaman ambos hooks
  // incondicionalmente; se usa el del modo activo.
  const cfgGeneral = useColumnasConfig('colcfg:productividad:general', META_GENERAL);
  const cfgMO = useColumnasConfig('colcfg:productividad:mo', META_MO, OCULTAS_MO);
  const config = modo === 'general' ? cfgGeneral : cfgMO;
  const meta = modo === 'general' ? META_GENERAL : META_MO;

  // Catálogo de partidas para los selectores (respeta filtros globales).
  const { data: catalogoResp } = usePartidasCatalogo();
  const padresOpts = useMemo(() => {
    const set = new Set<string>();
    for (const p of catalogoResp?.data ?? []) set.add(padreDe(p.taskNo));
    return [...set]
      .sort((a, b) => a.localeCompare(b, 'es', { numeric: true }))
      .map((v) => ({ value: v, label: v }));
  }, [catalogoResp]);
  // Partidas de las tareas padre seleccionadas (para el segundo multiselect).
  const partidasOpts = useMemo(() => {
    if (padresSel.size === 0) return [];
    return (catalogoResp?.data ?? [])
      .filter((p) => padresSel.has(padreDe(p.taskNo)))
      .sort((a, b) => a.taskNo.localeCompare(b.taskNo, 'es', { numeric: true }))
      .map((p) => ({ value: p.taskNo, label: etiquetaPartida(p) }));
  }, [catalogoResp, padresSel]);

  // Toggles con updates FUNCIONALES (dos clics seguidos no se pisan). No se
  // purga `partidasSel` al togglear padres: las partidas "huérfanas" (de un padre
  // deseleccionado) se ignoran vía `partidasEfectivas`.
  const togglePadre = (v: string) => {
    setPadresSel((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };
  const togglePartida = (v: string) => {
    setPartidasSel((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };
  const limpiarPartida = () => {
    setPadresSel(new Set());
    setPartidasSel(new Set());
  };

  // Partidas seleccionadas que aún pertenecen a un padre vigente.
  const partidasEfectivas = useMemo(
    () => new Set([...partidasSel].filter((t) => padresSel.has(padreDe(t)))),
    [partidasSel, padresSel],
  );

  const partidaSel = useMemo<PartidaSel | undefined>(() => {
    if (padresSel.size === 0 && partidasEfectivas.size === 0) return undefined;
    return {
      tareaPadre: padresSel.size ? [...padresSel].join(',') : undefined,
      partida: partidasEfectivas.size ? [...partidasEfectivas].join(',') : undefined,
    };
  }, [padresSel, partidasEfectivas]);

  const { data, isLoading, error } = useProductividad(partidaSel);

  const obras: FilaObra[] = useMemo(
    () =>
      (data?.data ?? []).map((o) => ({
        worksNo: o.worksNo,
        display: o.displayName,
        proy: o.bloque ?? '(sin proyecto)',
        bloque: o.subBloque ?? '(sin bloque)',
        areaM2Total: o.areaM2Total,
        m2Construidos: o.m2Construidos,
        pctAvance: o.pctAvance,
        costoDirecto: o.costoDirecto,
        presupuestoDirecto: o.presupuestoDirecto,
        costoAvance: o.costoAvance,
        costoRealXm2: o.costoRealXm2,
        presupuestoXm2: o.presupuestoXm2,
        costoMO: o.costoMO,
      })),
    [data],
  );

  // Derivadas General por obra.
  const derivGeneral = (o: FilaObra) => {
    const delta = o.costoRealXm2 - o.presupuestoXm2;
    const pctSobre = o.presupuestoXm2 > 0 ? delta / o.presupuestoXm2 : 0;
    const pctConsumido = o.presupuestoDirecto > 0 ? o.costoDirecto / o.presupuestoDirecto : 0;
    const idxEf = pctConsumido > 0 ? o.pctAvance / pctConsumido : 0;
    return { delta, pctSobre, idxEf, alerta: clasificar(pctSobre) };
  };
  // Derivadas M.O. por obra (con el estándar elegido).
  const derivMO = (o: FilaObra) => {
    const moXm2 = o.m2Construidos > 0 ? o.costoMO / o.m2Construidos : 0;
    const moTeoConsumida = o.m2Construidos * moEstandar;
    const moTeoTotal = o.areaM2Total * moEstandar;
    const desviacion = o.costoMO - moTeoConsumida;
    const idxEf = o.costoMO > 0 ? moTeoConsumida / o.costoMO : 0;
    const deltaXm2 = moEstandar - moXm2;
    return { moXm2, moTeoConsumida, moTeoTotal, desviacion, idxEf, deltaXm2 };
  };

  // Filtrar obras CON datos relevantes según el modo (ocultar las vacías).
  // Filtro global % Avance min/max (0–100). Solo aplica si se movió del rango
  // completo, para no excluir obras al pasar el rango por defecto.
  const { filtros: filtrosGlobales } = useFiltrosGlobales();
  const avMin = filtrosGlobales.pctAvanceMin ?? 0;
  const avMax = filtrosGlobales.pctAvanceMax ?? 100;
  const filtrarAvance = avMin > 0 || avMax < 100;

  const obrasVisibles = useMemo(() => {
    return obras.filter((o) => {
      // "Solo al 100%": casas completas = 100% de avance (tolerancia float) Y con
      // área registrada (un m² real; excluye obras sin área tipo SSCC/infra).
      if (solo100 && (o.pctAvance < 0.9995 || o.areaM2Total <= 0)) return false;
      // % Avance global.
      if (filtrarAvance) {
        const pct = o.pctAvance * 100;
        if (pct < avMin || pct > avMax) return false;
      }
      return modo === 'general'
        ? o.costoDirecto !== 0 || o.presupuestoDirecto !== 0
        : o.costoMO !== 0 || o.m2Construidos !== 0;
    });
  }, [obras, modo, solo100, filtrarAvance, avMin, avMax]);

  // Árbol Proyecto → Bloque → Obra, con agregados por nivel.
  const arbol = useMemo(() => {
    const proyectos = new Map<
      string,
      {
        proyecto: string;
        bloques: Map<string, { bloque: string; key: string; obras: FilaObra[] }>;
      }
    >();
    for (const o of obrasVisibles) {
      let p = proyectos.get(o.proy);
      if (!p) {
        p = { proyecto: o.proy, bloques: new Map() };
        proyectos.set(o.proy, p);
      }
      const bkey = `${o.proy}|${o.bloque}`;
      let b = p.bloques.get(bkey);
      if (!b) {
        b = { bloque: o.bloque, key: bkey, obras: [] };
        p.bloques.set(bkey, b);
      }
      b.obras.push(o);
    }
    const arr = [...proyectos.values()].sort((a, b) => a.proyecto.localeCompare(b.proyecto));
    return arr;
  }, [obrasVisibles]);

  // Agregar métricas de un grupo de obras (para filas Proyecto/Bloque).
  const agregarGeneral = (lista: FilaObra[]) => {
    const costoDir = lista.reduce((s, o) => s + o.costoDirecto, 0);
    const presupDir = lista.reduce((s, o) => s + o.presupuestoDirecto, 0);
    const m2 = lista.reduce((s, o) => s + o.m2Construidos, 0);
    const area = lista.reduce((s, o) => s + o.areaM2Total, 0);
    const costoRealXm2 = m2 > 0 ? costoDir / m2 : 0;
    const presupXm2 = area > 0 ? presupDir / area : 0;
    const delta = costoRealXm2 - presupXm2;
    const pctSobre = presupXm2 > 0 ? delta / presupXm2 : 0;
    // Índice eficiencia del grupo, ponderado y excluyendo obras sin avance
    // (m²=0). Eficiencia = Σ(costo avance esperado) / Σ(costo real), sobre las
    // obras con avance > 0. Equivale a %avance/%consumido a nivel grupo.
    const conAvance = lista.filter((o) => o.m2Construidos > 0);
    const costoEsperado = conAvance.reduce((s, o) => s + o.pctAvance * o.presupuestoDirecto, 0);
    const costoRealConAv = conAvance.reduce((s, o) => s + o.costoDirecto, 0);
    const idxEf = costoRealConAv > 0 ? costoEsperado / costoRealConAv : 0;
    return { costoRealXm2, presupXm2, delta, pctSobre, idxEf, alerta: clasificar(pctSobre) };
  };
  const agregarMO = (lista: FilaObra[]) => {
    const mo = lista.reduce((s, o) => s + o.costoMO, 0);
    const m2 = lista.reduce((s, o) => s + o.m2Construidos, 0);
    const area = lista.reduce((s, o) => s + o.areaM2Total, 0);
    const moXm2 = m2 > 0 ? mo / m2 : 0;
    const moTeoConsumida = m2 * moEstandar;
    const moTeoTotal = area * moEstandar;
    const desviacion = mo - moTeoConsumida;
    // Índice del grupo: Σ teórica / Σ costo, solo obras con avance (m²>0). Así un
    // costo sin avance (ej. VB-5.13) no distorsiona el ratio del bloque.
    const conAvance = lista.filter((o) => o.m2Construidos > 0);
    const moConAv = conAvance.reduce((s, o) => s + o.costoMO, 0);
    const teoConAv = conAvance.reduce((s, o) => s + o.m2Construidos * moEstandar, 0);
    const idxEf = moConAv > 0 ? teoConAv / moConAv : 0;
    const deltaXm2 = moEstandar - moXm2;
    return { mo, moXm2, moTeoConsumida, moTeoTotal, desviacion, idxEf, deltaXm2 };
  };

  // --- Definiciones de columnas por modo (celda + acceso a valor de orden) ---
  // Columnas de m²/avance compartidas por ambos modos.
  const defM2: Record<string, ColDef> = {
    m2Av: {
      obra: (o) => ({ texto: fmtNum(o.m2Construidos) }),
      grupo: (l) => ({ texto: fmtNum(sum(l, (o) => o.m2Construidos)) }),
      sortObra: (o) => o.m2Construidos,
      sortGrupo: (l) => sum(l, (o) => o.m2Construidos),
    },
    m2Tot: {
      obra: (o) => ({ texto: fmtNum(o.areaM2Total) }),
      grupo: (l) => ({ texto: fmtNum(sum(l, (o) => o.areaM2Total)) }),
      sortObra: (o) => o.areaM2Total,
      sortGrupo: (l) => sum(l, (o) => o.areaM2Total),
    },
    pctAv: {
      obra: (o) => ({ texto: o.areaM2Total > 0 ? fmtPct(o.pctAvance) : '—' }),
      grupo: (l) => {
        const area = sum(l, (o) => o.areaM2Total);
        return { texto: area > 0 ? fmtPct(sum(l, (o) => o.m2Construidos) / area) : '—' };
      },
      sortObra: (o) => o.pctAvance,
      sortGrupo: (l) => {
        const area = sum(l, (o) => o.areaM2Total);
        return area > 0 ? sum(l, (o) => o.m2Construidos) / area : 0;
      },
    },
  };

  const defsGeneral: Record<string, ColDef> = {
    ...defM2,
    costoReal: {
      obra: (o) => ({ texto: fmtNum(o.costoRealXm2) }),
      grupo: (l) => ({ texto: fmtNum(agregarGeneral(l).costoRealXm2) }),
      sortObra: (o) => o.costoRealXm2,
      sortGrupo: (l) => agregarGeneral(l).costoRealXm2,
    },
    costoTeo: {
      obra: (o) => ({ texto: fmtNum(o.presupuestoXm2) }),
      grupo: (l) => ({ texto: fmtNum(agregarGeneral(l).presupXm2) }),
      sortObra: (o) => o.presupuestoXm2,
      sortGrupo: (l) => agregarGeneral(l).presupXm2,
    },
    delta: {
      obra: (o) => ({ texto: fmtNum(derivGeneral(o).delta) }),
      grupo: (l) => ({ texto: fmtNum(agregarGeneral(l).delta) }),
      sortObra: (o) => derivGeneral(o).delta,
      sortGrupo: (l) => agregarGeneral(l).delta,
    },
    pctSobre: {
      obra: (o) => ({ texto: o.presupuestoXm2 > 0 ? fmtPct(derivGeneral(o).pctSobre) : '—' }),
      grupo: (l) => {
        const g = agregarGeneral(l);
        return { texto: g.presupXm2 > 0 ? fmtPct(g.pctSobre) : '—' };
      },
      sortObra: (o) => derivGeneral(o).pctSobre,
      sortGrupo: (l) => agregarGeneral(l).pctSobre,
    },
    idxEf: {
      obra: (o) => ({ texto: fmtIdx(derivGeneral(o).idxEf) }),
      grupo: (l) => ({ texto: fmtIdx(agregarGeneral(l).idxEf) }),
      sortObra: (o) => derivGeneral(o).idxEf,
      sortGrupo: (l) => agregarGeneral(l).idxEf,
    },
    estado: {
      obra: (o) => {
        const d = derivGeneral(o);
        const hay = o.presupuestoXm2 > 0 || o.costoRealXm2 > 0;
        return {
          texto: hay ? d.alerta.txt : '—',
          className: `font-semibold ${d.alerta.cls}`,
          title: hay ? d.alerta.titulo : undefined,
        };
      },
      grupo: (l) => {
        const g = agregarGeneral(l);
        return {
          texto: g.presupXm2 > 0 ? g.alerta.txt : '—',
          className: `font-semibold ${g.alerta.cls}`,
          title: g.presupXm2 > 0 ? g.alerta.titulo : undefined,
        };
      },
      sortObra: (o) => derivGeneral(o).alerta.orden,
      sortGrupo: (l) => agregarGeneral(l).alerta.orden,
    },
  };

  const defsMO: Record<string, ColDef> = {
    ...defM2,
    moXm2: {
      obra: (o) => ({ texto: fmtNum(derivMO(o).moXm2) }),
      grupo: (l) => ({ texto: fmtNum(agregarMO(l).moXm2) }),
      sortObra: (o) => derivMO(o).moXm2,
      sortGrupo: (l) => agregarMO(l).moXm2,
    },
    estandar: {
      obra: () => ({ texto: fmtNum(moEstandar) }),
      grupo: () => ({ texto: fmtNum(moEstandar) }),
      sortObra: () => moEstandar,
      sortGrupo: () => moEstandar,
    },
    deltaXm2: {
      obra: (o) => {
        const d = derivMO(o);
        return {
          texto: fmtNum(d.deltaXm2),
          className: d.deltaXm2 < 0 ? 'text-red-600' : 'text-emerald-700',
        };
      },
      grupo: (l) => {
        const g = agregarMO(l);
        return {
          texto: fmtNum(g.deltaXm2),
          className: g.deltaXm2 < 0 ? 'text-red-600' : 'text-emerald-700',
        };
      },
      sortObra: (o) => derivMO(o).deltaXm2,
      sortGrupo: (l) => agregarMO(l).deltaXm2,
    },
    costoMO: {
      obra: (o) => ({ texto: fmtMonto(o.costoMO) }),
      grupo: (l) => ({ texto: fmtMonto(agregarMO(l).mo) }),
      sortObra: (o) => o.costoMO,
      sortGrupo: (l) => agregarMO(l).mo,
    },
    moTeoCons: {
      obra: (o) => ({ texto: fmtMonto(derivMO(o).moTeoConsumida) }),
      grupo: (l) => ({ texto: fmtMonto(agregarMO(l).moTeoConsumida) }),
      sortObra: (o) => derivMO(o).moTeoConsumida,
      sortGrupo: (l) => agregarMO(l).moTeoConsumida,
    },
    desviacion: {
      obra: (o) => ({ texto: fmtMonto(derivMO(o).desviacion) }),
      grupo: (l) => ({ texto: fmtMonto(agregarMO(l).desviacion) }),
      sortObra: (o) => derivMO(o).desviacion,
      sortGrupo: (l) => agregarMO(l).desviacion,
    },
    idxEfMO: {
      obra: (o) => ({ texto: fmtIdx(derivMO(o).idxEf) }),
      grupo: (l) => ({ texto: fmtIdx(agregarMO(l).idxEf) }),
      sortObra: (o) => derivMO(o).idxEf,
      sortGrupo: (l) => agregarMO(l).idxEf,
    },
    moTeoTot: {
      obra: (o) => ({ texto: fmtMonto(derivMO(o).moTeoTotal) }),
      grupo: (l) => ({ texto: fmtMonto(agregarMO(l).moTeoTotal) }),
      sortObra: (o) => derivMO(o).moTeoTotal,
      sortGrupo: (l) => agregarMO(l).moTeoTotal,
    },
  };

  const defs = modo === 'general' ? defsGeneral : defsMO;
  const sortDef = config.sort ? defs[config.sort.id] : null;
  const sortDir = config.sort?.dir === 'asc' ? 1 : -1;
  // Las filas cuyo valor se muestra como "—" (sin dato) van SIEMPRE al final,
  // sin importar la dirección; el resto se ordena por el valor crudo.
  const cmpObras = (a: FilaObra, b: FilaObra) => {
    if (!sortDef) return a.worksNo.localeCompare(b.worksNo);
    const va = sortDef.obra(a).texto === '—';
    const vb = sortDef.obra(b).texto === '—';
    if (va !== vb) return va ? 1 : -1;
    if (va && vb) return a.worksNo.localeCompare(b.worksNo);
    return (sortDef.sortObra(a) - sortDef.sortObra(b)) * sortDir;
  };
  const cmpGrupos = (a: FilaObra[], b: FilaObra[]) => {
    if (!sortDef) return 0;
    const va = sortDef.grupo(a).texto === '—';
    const vb = sortDef.grupo(b).texto === '—';
    if (va !== vb) return va ? 1 : -1;
    if (va && vb) return 0;
    return (sortDef.sortGrupo(a) - sortDef.sortGrupo(b)) * sortDir;
  };

  // KPIs globales (sobre obras visibles). Cálculo barato y dependiente de
  // funciones recreadas por render → directo, sin useMemo.
  const kpis = (() => {
    if (modo === 'general') {
      const conPresup = obrasVisibles.filter((o) => o.presupuestoXm2 > 0);
      const pctDesvProm =
        conPresup.length > 0
          ? conPresup.reduce((s, o) => s + derivGeneral(o).pctSobre, 0) / conPresup.length
          : 0;
      const conIdx = obrasVisibles.filter((o) => derivGeneral(o).idxEf > 0);
      const idxProm =
        conIdx.length > 0
          ? conIdx.reduce((s, o) => s + derivGeneral(o).idxEf, 0) / conIdx.length
          : 0;
      const deltaTotal = obrasVisibles.reduce((s, o) => s + derivGeneral(o).delta, 0);
      return {
        a: fmtPct(pctDesvProm),
        aPos: pctDesvProm <= 0,
        b: fmtIdx(idxProm),
        bPos: idxProm >= 1,
        c: fmtMonto(deltaTotal),
        cPos: deltaTotal <= 0,
      };
    }
    const g = agregarMO(obrasVisibles);
    // Costo M.O. x m² global = Σ costo M.O. / Σ m² construidos (obras con avance).
    return {
      a: fmtNum(g.moXm2),
      aPos: g.moXm2 <= moEstandar,
      b: fmtIdx(g.idxEf),
      bPos: g.idxEf >= 1,
      c: fmtMonto(g.desviacion),
      cPos: g.desviacion <= 0,
    };
  })();

  const toggle = (key: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const todasKeys = useMemo(() => {
    const k = new Set<string>();
    for (const p of arbol) {
      k.add(`P|${p.proyecto}`);
      for (const b of p.bloques.values()) k.add(`B|${b.key}`);
    }
    return k;
  }, [arbol]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const visibles = config.visibles;
  const etiquetaDe = new Map(meta.map((c) => [c.id, c.etiqueta]));

  // Celdas de datos de una obra / grupo, según columnas visibles y su orden.
  const celdasDe = (fuente: FilaObra | FilaObra[]): ReactElement[] =>
    visibles.map((id) => {
      const def = defs[id];
      const c = def
        ? Array.isArray(fuente)
          ? def.grupo(fuente)
          : def.obra(fuente)
        : { texto: '' };
      return (
        <TableCell
          key={id}
          title={c.title}
          className={`text-right text-xs tabular-nums ${c.className ?? ''}`}
        >
          {c.texto}
        </TableCell>
      );
    });

  // Construir las filas del árbol, aplicando el ordenamiento activo.
  const filas: ReactElement[] = [];
  const todasObras: FilaObra[] = [];
  const proyectosOrdenados = [...arbol].sort((pa, pb) => {
    if (!sortDef) return 0; // ya vienen alfabéticos
    const la = [...pa.bloques.values()].flatMap((b) => b.obras);
    const lb = [...pb.bloques.values()].flatMap((b) => b.obras);
    return cmpGrupos(la, lb);
  });
  for (const p of proyectosOrdenados) {
    const pKey = `P|${p.proyecto}`;
    const pOpen = expandidos.has(pKey);
    const obrasP = [...p.bloques.values()].flatMap((b) => b.obras);
    todasObras.push(...obrasP);
    filas.push(
      <TableRow key={pKey} className="bg-emerald-100 font-semibold">
        <TableCell className="sticky left-0 z-10 bg-emerald-100 text-xs">
          <button
            type="button"
            onClick={() => toggle(pKey)}
            className="flex items-center gap-1 hover:underline"
          >
            {pOpen ? (
              <CaretDown className="h-3 w-3" weight="bold" />
            ) : (
              <CaretRight className="h-3 w-3" weight="bold" />
            )}
            {p.proyecto}
          </button>
        </TableCell>
        {celdasDe(obrasP)}
      </TableRow>,
    );
    if (!pOpen) continue;
    const bloquesOrdenados = [...p.bloques.values()].sort((ba, bb) =>
      sortDef ? cmpGrupos(ba.obras, bb.obras) : ba.bloque.localeCompare(bb.bloque),
    );
    for (const b of bloquesOrdenados) {
      const bKey = `B|${b.key}`;
      const bOpen = expandidos.has(bKey);
      filas.push(
        <TableRow key={bKey} className="bg-emerald-50 font-medium">
          <TableCell className="sticky left-0 z-10 bg-emerald-50 text-xs">
            <button
              type="button"
              onClick={() => toggle(bKey)}
              className="flex items-center gap-1 pl-4 hover:underline"
            >
              {bOpen ? (
                <CaretDown className="h-3 w-3" weight="bold" />
              ) : (
                <CaretRight className="h-3 w-3" weight="bold" />
              )}
              {b.bloque}
            </button>
          </TableCell>
          {celdasDe(b.obras)}
        </TableRow>,
      );
      if (!bOpen) continue;
      const obrasOrdenadas = [...b.obras].sort(cmpObras);
      for (const o of obrasOrdenadas) {
        filas.push(
          <TableRow key={`O|${o.worksNo}`}>
            <TableCell className="sticky left-0 z-10 bg-background text-xs">
              <div className="pl-9">
                <div className="font-medium">{o.worksNo}</div>
                {o.display && o.display !== o.worksNo && (
                  <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">
                    {o.display}
                  </div>
                )}
              </div>
            </TableCell>
            {celdasDe(o)}
          </TableRow>,
        );
      }
    }
  }

  // Exportar a Excel: recorre el árbol COMPLETO (todos los niveles), respetando
  // las columnas visibles + su orden + el ordenamiento activo, con subtotales por
  // nivel y Gran Total. Respeta también los filtros de partida / "Solo al 100%" /
  // globales, porque parten del mismo `arbol`/`obrasVisibles`.
  const exportar = () => {
    const cols = visibles.map((id) => ({
      id,
      etiqueta: etiquetaDe.get(id) ?? id,
      fmt: FMT_COLUMNA[id] ?? 'numero',
    }));
    const celdasExport = (fuente: FilaObra | FilaObra[]) =>
      cols.map(({ id, fmt }) => {
        const def = defs[id];
        if (!def) return { valor: null };
        const c = Array.isArray(fuente) ? def.grupo(fuente) : def.obra(fuente);
        if (c.texto === '—') return { valor: null };
        if (fmt === 'texto') return { valor: c.texto };
        return { valor: Array.isArray(fuente) ? def.sortGrupo(fuente) : def.sortObra(fuente) };
      });

    const filasExport: FilaCostosM2Export[] = [];
    const todas: FilaObra[] = [];
    for (const p of proyectosOrdenados) {
      const obrasP = [...p.bloques.values()].flatMap((b) => b.obras);
      todas.push(...obrasP);
      filasExport.push({
        depth: 0,
        etiqueta: p.proyecto,
        esSubtotal: true,
        celdas: celdasExport(obrasP),
      });
      const bloques = [...p.bloques.values()].sort((ba, bb) =>
        sortDef ? cmpGrupos(ba.obras, bb.obras) : ba.bloque.localeCompare(bb.bloque),
      );
      for (const b of bloques) {
        filasExport.push({
          depth: 1,
          etiqueta: b.bloque,
          esSubtotal: true,
          celdas: celdasExport(b.obras),
        });
        for (const o of [...b.obras].sort(cmpObras)) {
          const etiqueta =
            o.display && o.display !== o.worksNo ? `${o.worksNo} — ${o.display}` : o.worksNo;
          filasExport.push({ depth: 2, etiqueta, esSubtotal: false, celdas: celdasExport(o) });
        }
      }
    }
    filasExport.push({
      depth: 0,
      etiqueta: 'Gran Total',
      esSubtotal: false,
      esGranTotal: true,
      celdas: celdasExport(todas),
    });

    exportarCostosM2Excel({
      nombreArchivo: `costos-m2-${modo === 'general' ? 'directo' : 'mano-obra'}`,
      tituloHoja: modo === 'general' ? 'Costos m2 (Costo Directo)' : 'Costos m2 (Mano de Obra)',
      encabezadoJerarquia: 'Proyecto / Bloque / Obra',
      columnas: cols.map(({ etiqueta, fmt }) => ({ etiqueta, fmt })),
      filas: filasExport,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {modo === 'general'
            ? 'Costos/m² (Costo Directo)'
            : modo === 'mo'
              ? 'Costos/m² — Mano de Obra'
              : 'Costos/m² — Matriz por Partida'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {modo === 'general'
            ? 'Costo real directo por m² construido vs costo teórico (presupuesto directo / área). Sobrecosto si supera el teórico; Subcosteo si está muy por debajo (probable falta de gastos por registrar).'
            : modo === 'mo'
              ? 'Costo de mano de obra (cuenta 6010060000000) por m² construido vs un estándar configurable. La M.O. teórica = m² × estándar.'
              : 'Matriz de Costo/m² por partida de vivienda (tareas padre 1–4): filas Proyecto/Obra, columnas por partida. Elegí variante (General / Mano de Obra / Materiales) y denominador (m² construidos o totales).'}{' '}
          Acumulado al mes más reciente del filtro Períodos (o al día si no hay período
          seleccionado). Respeta los filtros globales.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
            <button
              type="button"
              onClick={() => setModo('general')}
              className={`px-2.5 py-1 ${modo === 'general' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              General
            </button>
            <button
              type="button"
              onClick={() => setModo('mo')}
              className={`px-2.5 py-1 ${modo === 'mo' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Mano de Obra
            </button>
            <button
              type="button"
              onClick={() => setModo('matriz')}
              className={`px-2.5 py-1 ${modo === 'matriz' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Matriz × Partida
            </button>
          </div>
          {modo !== 'matriz' && (
            <>
              {modo === 'mo' && (
                <label className="flex items-center gap-2 text-zinc-600">
                  <span>MO Estándar x m²:</span>
                  <input
                    type="number"
                    value={moEstandar}
                    onChange={(e) => setMoEstandar(Number(e.target.value) || 0)}
                    className="w-28 rounded border border-zinc-300 px-2 py-0.5 text-right tabular-nums"
                    step={1000}
                    min={0}
                  />
                  <input
                    type="range"
                    value={moEstandar}
                    onChange={(e) => setMoEstandar(Number(e.target.value))}
                    min={0}
                    max={300000}
                    step={1000}
                    className="w-40"
                  />
                </label>
              )}
              <span className="text-zinc-300">|</span>
              <button
                type="button"
                onClick={() => setExpandidos(new Set(todasKeys))}
                className="text-blue-600 hover:underline"
              >
                Expandir todo
              </button>
              <span className="text-zinc-300">|</span>
              <button
                type="button"
                onClick={() => setExpandidos(new Set())}
                className="text-blue-600 hover:underline"
              >
                Colapsar
              </button>
              <span className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={exportar}
                  className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50"
                  title="Exportar el reporte a Excel (columnas visibles, filtros y orden actuales)"
                >
                  <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
                  Exportar a Excel
                </button>
                <GestorColumnas columnas={meta} config={config} />
              </span>
            </>
          )}
        </div>

        {/* Filtro de partida (jerárquico, local al reporte), multi-selección */}
        {modo !== 'matriz' && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <label
              className="flex items-center gap-1.5 text-zinc-600"
              title="Mostrar solo obras con 100% de avance"
            >
              <input
                type="checkbox"
                checked={solo100}
                onChange={(e) => setSolo100(e.target.checked)}
              />
              Solo al 100%
            </label>
            <span className="text-zinc-300">|</span>
            <span className="text-muted-foreground">Partida:</span>
            <MultiSelectPopup
              titulo="Tarea Padre"
              opciones={padresOpts}
              seleccion={padresSel}
              onToggle={togglePadre}
              onNinguna={limpiarPartida}
            />
            <MultiSelectPopup
              titulo="Partida"
              opciones={partidasOpts}
              seleccion={partidasEfectivas}
              onToggle={togglePartida}
              onTodas={() => setPartidasSel(new Set(partidasOpts.map((o) => o.value)))}
              onNinguna={() => setPartidasSel(new Set())}
              disabled={padresSel.size === 0}
              ancho="w-80"
            />
            {(padresSel.size > 0 || partidasEfectivas.size > 0) && (
              <>
                <button
                  type="button"
                  onClick={limpiarPartida}
                  className="text-blue-600 hover:underline"
                >
                  Limpiar
                </button>
                <span className="text-emerald-700">
                  Costo x m² restringido a{' '}
                  {partidasEfectivas.size > 0
                    ? `${partidasEfectivas.size} partida(s)`
                    : `${padresSel.size} tarea(s) padre`}{' '}
                  (m² = de la obra).
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {modo === 'matriz' && <MatrizCostoM2 />}

      {modo !== 'matriz' && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {modo === 'general' ? (
              <>
                <KpiCard titulo="% Desviación x m² (Directo)" valor={kpis.a} pos={kpis.aPos} />
                <KpiCard
                  titulo="Índice Eficiencia Directo (Prom.)"
                  valor={kpis.b}
                  pos={kpis.bPos}
                />
                <KpiCard titulo="Delta Costo (₡ x m²)" valor={kpis.c} pos={kpis.cPos} />
              </>
            ) : (
              <>
                <KpiCard titulo="Costo M.O. x m² (global)" valor={kpis.a} pos={kpis.aPos} />
                <KpiCard titulo="Índice Eficiencia MO (Global)" valor={kpis.b} pos={kpis.bPos} />
                <KpiCard titulo="Desviación MO" valor={kpis.c} pos={kpis.cPos} />
              </>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              <Table wrapperClassName="max-h-[calc(100vh-320px)]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 top-0 z-30 bg-background min-w-[200px]">
                      Proyecto / Bloque / Obra
                    </TableHead>
                    {visibles.map((id) => {
                      const activo = config.sort?.id === id;
                      const flecha = activo ? (config.sort?.dir === 'desc' ? ' ▼' : ' ▲') : '';
                      return (
                        <TableHead
                          key={id}
                          className="sticky top-0 z-20 bg-background text-right text-xs"
                        >
                          <button
                            type="button"
                            onClick={() => config.setSort(id)}
                            className="inline-flex w-full items-center justify-end gap-0.5 hover:underline"
                            title="Ordenar por esta columna"
                          >
                            {etiquetaDe.get(id) ?? id}
                            <span className="text-emerald-600">{flecha}</span>
                          </button>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filas}
                  {todasObras.length > 0 && (
                    <TableRow className="border-t-2 border-zinc-300 bg-zinc-100 font-bold">
                      <TableCell className="sticky left-0 z-10 bg-zinc-100 text-xs">
                        Gran Total
                      </TableCell>
                      {celdasDe(todasObras)}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KpiCard({ titulo, valor, pos }: { titulo: string; valor: string; pos: boolean | null }) {
  const color = pos === null ? 'text-zinc-800' : pos ? 'text-emerald-700' : 'text-red-600';
  return (
    <Card>
      <CardContent className="pt-4">
        <p className="text-xs text-muted-foreground">{titulo}</p>
        <p className={`mt-1 text-2xl font-semibold tabular-nums ${color}`}>{valor}</p>
      </CardContent>
    </Card>
  );
}
